/**
 * component-product-form.js -- Variant selection + Add to cart for Mirai Online (Phase 3)
 *
 * Defines TWO Web Components (kept in one file per RESEARCH anti-pattern guidance):
 *
 *  <variant-selector> -- reads the embedded product JSON (no fetch on click) to update
 *    price, compare-at, hidden variant id, the ?variant= URL, and cross-variant
 *    sold-out availability (Pitfall 1: always re-derive from variants, never trust load state).
 *
 *  <add-to-cart-form> -- validates explicit size/colour selection (Pitfall 3), POSTs to
 *    /cart/add.js, and opens the existing Phase 1 cart drawer via
 *    window.CartDrawerHelpers.openCartDrawerWithContent. Button disabled during the
 *    in-flight request (DoS guard T-03-08).
 *
 *  Light DOM only (no Shadow DOM). Both guarded with customElements.get(...).
 *
 * Security notes:
 *  - Variant id is sent to Shopify's /cart/add.js; the server validates inventory and
 *    returns 422 for sold-out, so no client-side availability is authoritative (T-03-05).
 *  - Uses window.Theme.routes.cart_add_url (Shopify CSRF-protected theme endpoint, T-03-06).
 */

/* ============================================================
   VARIANT SELECTOR
   ============================================================ */

class VariantSelector extends HTMLElement {
  constructor() {
    super();
    this._boundClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this._init();
    this.addEventListener('shopify:section:load', () => this._reinit());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundClick);
  }

  _init() {
    const sectionId = this.dataset.sectionId;
    const dataEl = document.getElementById('product-data-' + sectionId);
    try {
      this._product = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (e) {
      this._product = null;
    }
    if (!this._product) return;

    // Scope to the PDP section or, when reused inside the Quick View modal, to the
    // .quick-view container — never `document`, so a quick view opened on a PDP page
    // reads its OWN price/variant-id/quantity, not the underlying product's.
    this._section = this.closest('.main-product, .quick-view') || document;
    this._optionGroups = Array.from(this.querySelectorAll('[data-option-index]')).filter(
      (el) => el.tagName === 'FIELDSET'
    );

    // Build initial selection from the pressed buttons (Liquid SSR state)
    this._selectedOptions = [];
    this._optionGroups.forEach((group) => {
      const oi = parseInt(group.dataset.optionIndex, 10);
      const pressed = group.querySelector('[aria-pressed="true"]');
      this._selectedOptions[oi] = pressed ? pressed.dataset.value : null;
    });

    this.addEventListener('click', this._boundClick);
  }

  _reinit() {
    this.removeEventListener('click', this._boundClick);
    this._init();
  }

  _handleClick(e) {
    const btn = e.target.closest('[data-value]');
    if (!btn || btn.disabled) return;

    const group = btn.closest('[data-option-index]');
    if (!group) return;
    const oi = parseInt(group.dataset.optionIndex, 10);

    // Mark explicit user selection (used by add-to-cart validation)
    group.setAttribute('data-user-selected', '');

    // Update pressed state within this option group
    group.querySelectorAll('[data-value]').forEach((b) => {
      const isThis = b === btn;
      b.setAttribute('aria-pressed', isThis ? 'true' : 'false');
      b.classList.toggle('variant-btn--selected', isThis && b.classList.contains('variant-btn'));
      b.classList.toggle('swatch-btn--selected', isThis && b.classList.contains('swatch-btn'));
    });

    this._selectedOptions[oi] = btn.dataset.value;

    // Update the "Size: X" selected-value label
    const selectedLabel = group.querySelector('[data-selected-value]');
    if (selectedLabel) selectedLabel.textContent = ': ' + btn.dataset.value;

    const variant = this._findVariant(this._selectedOptions);
    this._updateForVariant(variant);
    this._updateAvailability();
  }

  _findVariant(selected) {
    if (!this._product) return null;
    // Require a full, exact match across every option dimension. A partial
    // match (any null slot) is treated as "no variant" so price/compare-at/
    // variant-id never reflect a variant the shopper did not fully choose.
    if (selected.some((v) => v == null)) return null;
    return (
      this._product.variants.find((v) =>
        v.options.every((opt, idx) => opt === selected[idx])
      ) || null
    );
  }

  _updateForVariant(variant) {
    const priceEl = this._section.querySelector('[data-price]');
    const compareEl = this._section.querySelector('[data-compare-price]');
    const variantIdInput = this._section.querySelector('[data-variant-id]');

    if (!variant) {
      // Unavailable combination — leave price, disable add to cart
      const addBtn = this._section.querySelector('[data-add-to-cart]');
      if (addBtn) addBtn.disabled = true;
      return;
    }

    if (priceEl) priceEl.textContent = this._formatMoney(variant.price);
    if (compareEl) {
      const amountEl = compareEl.querySelector('[data-compare-amount]');
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (amountEl) amountEl.textContent = this._formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
    if (variantIdInput) variantIdInput.value = variant.id;

    // Re-enable add to cart if this variant is purchasable
    const addBtn = this._section.querySelector('[data-add-to-cart]');
    if (addBtn) addBtn.disabled = !variant.available;

    // URL update (no reload)
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this._updateGalleryImage(variant);
  }

  _updateGalleryImage(variant) {
    if (!variant.featured_image || !variant.featured_image.src) return;
    try {
      const base = variant.featured_image.src.split('?')[0].split('/').pop();
      const images = Array.from(this._section.querySelectorAll('[data-gallery-image]'));
      const match = images.find((img) => {
        const src = img.currentSrc || img.src || '';
        const hi = img.dataset.highres || '';
        return src.indexOf(base) > -1 || hi.indexOf(base) > -1;
      });
      if (!match) return;

      // Prefer the gallery's own activation (switches the desktop Amazon stage,
      // its thumbnail, and the mobile carousel together); fall back to a plain
      // scroll when the gallery component isn't present (e.g. quick view).
      const gallery = this._section.querySelector('product-gallery');
      const item = match.closest('[data-gallery-item]');
      const idx = item ? parseInt(item.dataset.index, 10) : NaN;
      if (gallery && typeof gallery.setActive === 'function' && !isNaN(idx)) {
        gallery.setActive(idx);
      } else if (typeof match.scrollIntoView === 'function') {
        match.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    } catch (e) {
      /* gallery sync is a non-critical enhancement */
    }
  }

  /**
   * Cross-variant availability (Pitfall 1): for each option value, mark it sold-out
   * when no available variant exists that pairs it with the currently-selected other options.
   */
  _updateAvailability() {
    let selectionCleared = false;

    this._optionGroups.forEach((group) => {
      const oi = parseInt(group.dataset.optionIndex, 10);
      group.querySelectorAll('[data-value]').forEach((btn) => {
        const val = btn.dataset.value;
        const available = this._product.variants.some(
          (v) =>
            v.options[oi] === val &&
            v.available &&
            this._selectedOptions.every((sel, idx) => idx === oi || sel == null || v.options[idx] === sel)
        );
        const isSwatch = btn.classList.contains('swatch-btn');
        btn.classList.toggle(isSwatch ? 'swatch-btn--sold-out' : 'variant-btn--sold-out', !available);
        btn.disabled = !available;
        if (!available) {
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
        }

        // If the value the shopper currently has selected just became
        // unavailable, clear its pressed/selected state and drop it from
        // _selectedOptions so the UI does not show it as selected + sold-out
        // and _findVariant stops resolving against a dead value (WR-02).
        if (!available && this._selectedOptions[oi] === val && btn.getAttribute('aria-pressed') === 'true') {
          btn.setAttribute('aria-pressed', 'false');
          btn.classList.remove('variant-btn--selected', 'swatch-btn--selected');
          this._selectedOptions[oi] = null;
          selectionCleared = true;

          const selectedLabel = group.querySelector('[data-selected-value]');
          if (selectedLabel) selectedLabel.textContent = '';
        }
      });
    });

    // A cleared selection means price/variant-id/add-to-cart state must be
    // re-derived from the (now partial) selection.
    if (selectionCleared) {
      this._updateForVariant(this._findVariant(this._selectedOptions));
    }
  }

  /**
   * Minimal money formatter. Prices in the embedded JSON are integer cents.
   * Reads currency/locale from window.Theme.settings when available; defaults to INR.
   */
  _formatMoney(cents) {
    const amount = (cents || 0) / 100;
    const currency = (window.Theme && window.Theme.settings && window.Theme.settings.currency) || 'INR';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2
      }).format(amount);
    } catch (e) {
      return '₹' + amount.toLocaleString();
    }
  }
}

/* ============================================================
   ADD TO CART FORM
   ============================================================ */

class AddToCartForm extends HTMLElement {
  constructor() {
    super();
    this._boundSubmit = this._handleSubmit.bind(this);
    this._boundBuyNow = this._handleBuyNow.bind(this);
    this._loading = false;
  }

  connectedCallback() {
    this._init();
    this.addEventListener('shopify:section:load', () => this._reinit());
  }

  disconnectedCallback() {
    if (this._form) this._form.removeEventListener('submit', this._boundSubmit);
    if (this._buyNowButton) this._buyNowButton.removeEventListener('click', this._boundBuyNow);
  }

  _init() {
    this._section = this.closest('.main-product, .quick-view') || document;
    this._form = this.querySelector('form');
    this._button = this.querySelector('[data-add-to-cart]');
    this._buyNowButton = this.querySelector('[data-buy-now]');
    this._errorEl = this.querySelector('[data-form-error]');
    this._strings = {
      add: this.dataset.strAdd || 'Add to cart',
      adding: this.dataset.strAdding || 'Adding...',
      buyNow: this.dataset.strBuyNow || 'Buy it now',
      redirecting: this.dataset.strRedirecting || 'Redirecting…',
      selectSize: this.dataset.strSelectSize || 'Please select a size',
      selectColor: this.dataset.strSelectColor || 'Please select an option',
      selectOption: this.dataset.strSelectOption || 'Please choose an option',
      outOfStock: this.dataset.strOutOfStock || 'This item is currently out of stock',
      generic: this.dataset.strGeneric || 'Something went wrong. Please try again.'
    };
    if (this._form) this._form.addEventListener('submit', this._boundSubmit);
    if (this._buyNowButton) this._buyNowButton.addEventListener('click', this._boundBuyNow);
  }

  _reinit() {
    if (this._form) this._form.removeEventListener('submit', this._boundSubmit);
    if (this._buyNowButton) this._buyNowButton.removeEventListener('click', this._boundBuyNow);
    this._init();
  }

  /**
   * Shared validation for both Add to Cart and Buy Now. Enforces explicit
   * option selection (Pitfall 3) and resolves the variant id + quantity.
   * Returns { variantId, quantity } or null (and surfaces an error) on failure.
   */
  _getValidatedOrder() {
    this._clearError();

    // Validation: every option group must be explicitly chosen by the user (Pitfall 3)
    const groups = Array.from(this._section.querySelectorAll('.variant-option'));
    for (const group of groups) {
      if (group.hasAttribute('data-user-selected')) continue;
      const name = (group.dataset.optionName || '').toLowerCase();
      const isSwatch = group.classList.contains('variant-option--swatch');
      // Only enforce when there is a real choice (more than one value)
      const values = group.querySelectorAll('[data-value]');
      if (values.length <= 1) continue;
      if (name.indexOf('size') > -1) {
        this._showError(this._strings.selectSize);
        return null;
      }
      if (isSwatch) {
        this._showError(this._strings.selectColor);
        return null;
      }
      // Generic fallback: any other multi-value option group (e.g. Length,
      // Sleeve) that was never explicitly chosen must also block submission
      // so it cannot silently default to the SSR value (Pitfall 3, WR-07).
      this._showError(this._strings.selectOption);
      return null;
    }

    const variantInput = this._section.querySelector('[data-variant-id]');
    const variantId = variantInput ? parseInt(variantInput.value, 10) : NaN;
    if (!variantId || isNaN(variantId)) {
      this._showError(this._strings.generic);
      return null;
    }

    // Optional quantity (quick-view stepper). Absent on the PDP → defaults to 1.
    const qtyInput = this._section.querySelector('[data-quantity]');
    let quantity = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!quantity || isNaN(quantity) || quantity < 1) quantity = 1;

    return { variantId: variantId, quantity: quantity };
  }

  _handleSubmit(e) {
    e.preventDefault();
    if (this._loading) return;

    const order = this._getValidatedOrder();
    if (!order) return;

    this._setLoading(true);

    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: order.variantId, quantity: order.quantity }],
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then((r) => r.json().then((body) => ({ status: r.status, body: body })))
      .then(({ status, body }) => {
        if (status === 422) {
          this._showError(body.description || this._strings.outOfStock);
          return;
        }
        if (status >= 400) {
          this._showError(body.description || this._strings.generic);
          return;
        }
        if (window.CartDrawerHelpers && window.CartDrawerHelpers.openCartDrawerWithContent) {
          window.CartDrawerHelpers.openCartDrawerWithContent({
            triggerElement: this._button,
            sections: body.sections
          });
        }
      })
      .catch(() => {
        this._showError(this._strings.generic);
      })
      .finally(() => {
        this._setLoading(false);
      });
  }

  /**
   * Buy Now — add the selected variant to the cart, then send the shopper
   * straight to checkout. Reuses the same validation as Add to Cart, and the
   * same in-flight guard so a double-tap can't double-add (T-03-08).
   */
  _handleBuyNow(e) {
    if (e) e.preventDefault();
    if (this._loading) return;

    const order = this._getValidatedOrder();
    if (!order) return;

    this._setBuyNowLoading(true);

    const url =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: order.variantId, quantity: order.quantity }] })
    })
      .then((r) => r.json().then((body) => ({ status: r.status, body: body })))
      .then(({ status, body }) => {
        if (status === 422) {
          this._showError(body.description || this._strings.outOfStock);
          this._setBuyNowLoading(false);
          return;
        }
        if (status >= 400) {
          this._showError(body.description || this._strings.generic);
          this._setBuyNowLoading(false);
          return;
        }
        // Success — leave the button in its loading state and navigate. The
        // page unload tears the component down, so there's no flicker back.
        window.location.href = '/checkout';
      })
      .catch(() => {
        this._showError(this._strings.generic);
        this._setBuyNowLoading(false);
      });
  }

  _setBuyNowLoading(on) {
    this._loading = on;
    if (this._button) this._button.disabled = on;
    if (!this._buyNowButton) return;
    this._buyNowButton.disabled = on;
    this._buyNowButton.textContent = on ? this._strings.redirecting : this._strings.buyNow;
  }

  _setLoading(on) {
    this._loading = on;
    if (!this._button) return;
    this._button.disabled = on;
    this._button.textContent = on ? this._strings.adding : this._strings.add;
  }

  _showError(message) {
    if (!this._errorEl) return;
    this._errorEl.textContent = message;
    this._errorEl.hidden = false;
  }

  _clearError() {
    if (!this._errorEl) return;
    this._errorEl.textContent = '';
    this._errorEl.hidden = true;
  }
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}
if (!customElements.get('add-to-cart-form')) {
  customElements.define('add-to-cart-form', AddToCartForm);
}
